import type { VideoComposition } from "@hightouch/ad-studio-renderer";
import { createContext, useContext, useEffect, useMemo, useRef } from "react";

import {
  type AmpLibraryCanvasAdsQuery,
  type AmpLibraryFileFragment,
  useAmpLibraryCanvasAdsQuery,
} from "src/graphql";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import { isDecomposing } from "src/pages/ad-studio/briefs/image-state";

/** A full-width ad image layer at the top zoom on a 2x screen, the same width email and web page tiles load. */
export const AD_TILE_IMAGE_WIDTH = 1200;

const isAnyAdDecomposing = (data: AmpLibraryCanvasAdsQuery | undefined) =>
  data?.ad_brief_assets.some((ad) => isDecomposing(ad.image_state)) ?? false;

type CanvasAdRow = AmpLibraryCanvasAdsQuery["ad_brief_assets"][number];

export type CanvasAd = Omit<
  CanvasAdRow,
  "component_assets" | "component_asset_urls" | "video_composition"
> & {
  component_assets: Record<string, string> | null;
  component_asset_urls: { urls: Record<string, string> | null };
  video_composition: VideoComposition | null;
};

export type CanvasAds = {
  ads: ReadonlyMap<string, CanvasAd>;
  /** The ads on the canvas are still being read, so one missing from `ads` may yet arrive. */
  isLoading: boolean;
};

const CanvasAdsContext = createContext<CanvasAds>({
  ads: new Map(),
  isLoading: false,
});

export const CanvasAdsProvider = CanvasAdsContext.Provider;

/** The ad behind a canvas ad file, and whether the canvas is still fetching it. */
export function useCanvasAd(assetId: string | undefined): {
  ad: CanvasAd | undefined;
  isLoading: boolean;
} {
  const { ads, isLoading } = useContext(CanvasAdsContext);
  const ad = assetId ? ads.get(assetId) : undefined;
  return { ad, isLoading: Boolean(assetId) && !ad && isLoading };
}

/**
 * What a canvas ad file's card previews: the ad the canvas read, or the file's own copy of it until then.
 * While `isLoading`, the card shows a skeleton because the file's copy may be an older version.
 */
export function useCanvasAdPreview(file: Pick<AmpLibraryFileFragment, "ad">) {
  const { ad, isLoading } = useCanvasAd(file.ad?.assetId);
  const preview =
    ad ??
    (file.ad ? { asset_url: file.ad.previewUrl, svg: file.ad.svg } : null);
  return { preview, isLoading };
}

/**
 * Fetches every ad on the canvas in one query, and again when one of them
 * gets a new version, which the canvas's own polling notices. Polls while an
 * ad is still being split into layers.
 */
export function useCanvasAds(
  ads: { assetId: string; versionId: string | null }[],
): CanvasAds {
  const idsKey = [...new Set(ads.map((ad) => ad.assetId))].sort().join(",");
  const versionsKey = ads
    .map((ad) => `${ad.assetId}:${ad.versionId}`)
    .sort()
    .join(",");
  const ids = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);
  const { refetchInterval } =
    useGovernedRefetchInterval<AmpLibraryCanvasAdsQuery>({
      baseMs: 3000,
      maxMs: 10000,
      compareKey: (data) =>
        data?.ad_brief_assets.map((ad) => [
          ad.id,
          ad.version_id,
          ad.image_state,
        ]),
    });
  const { data, refetch, isLoading, isPreviousData } =
    useAmpLibraryCanvasAdsQuery(
      { ids },
      {
        enabled: ids.length > 0,
        keepPreviousData: true,
        // A split into layers that fails or is skipped leaves the ad's version as it was, so the canvas's own polling never sees it end.
        refetchInterval: (data, query) =>
          isAnyAdDecomposing(data) ? refetchInterval(data, query) : false,
      },
    );

  // A changed set of ads is a new query key and fetches by itself; only a new version of the same ads needs a refetch.
  const seen = useRef({ idsKey, versionsKey });
  useEffect(() => {
    const previous = seen.current;
    seen.current = { idsKey, versionsKey };
    if (previous.idsKey === idsKey && previous.versionsKey !== versionsKey)
      void refetch();
  }, [idsKey, versionsKey, refetch]);

  const byId = useMemo(
    () =>
      new Map(
        (data?.ad_brief_assets ?? []).map((ad) => [
          ad.id,
          {
            ...ad,
            video_composition: ad.video_composition as VideoComposition | null,
            // jsonb: the asset's component keys, by ref.
            component_assets: ad.component_assets as Record<
              string,
              string
            > | null,
            // A signed URL per component asset, by ref. Hasura returns null when the ad has no version yet.
            component_asset_urls: (ad.component_asset_urls ?? {
              urls: null,
            }) as { urls: Record<string, string> | null },
          },
        ]),
      ),
    [data],
  );
  // While a new ad's query runs, the previous set's data stands in without it.
  const isFetchingAds = ids.length > 0 && (isLoading || isPreviousData);
  return useMemo(
    () => ({ ads: byId, isLoading: isFetchingAds }),
    [byId, isFetchingAds],
  );
}
